import { useSelector } from 'react-redux'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { Map } from 'immutable'
import _startCase from 'lodash/startCase'
import { parse as parseQuery, stringify as stringifyQuery } from '../../services/query-string/index'
import {
  PLAY_CLICK_GA4_EVENT,
  TAG_CLICKED_GA4_EVENT,
  RESTART_SUBSCRIPTION_CLICKED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { setEventDefault } from '../../services/event-tracking/actions'
import { selectUpstreamContext } from '../../services/upstream-context/selectors'
import { STORE_KEY_DETAIL_FEATURED_EPISODE } from '../../services/store-keys/index'
import {
  createUpstreamContext,
  SCREEN_TYPE_DETAIL_SERIES,
  SCREEN_TYPE_DETAIL_VIDEO,
  CONTEXT_TYPE_VIDEO_ENTITY,
} from '../../services/upstream-context/index'
import {
  SUBTYPE_CONTENT_MEDITATION,
  SUBTYPE_CONTENT_YOGA,
  SUBTYPE_CONTENT_FITNESS,
  TYPE_CONTENT_EPISODE,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
} from '../../services/content-type/index'
import {
  DETAIL_PAGE_WATCH_LABEL_CONTINUE,
  DETAIL_PAGE_WATCH_LABEL_FIRST,
  DETAIL_PAGE_WATCH_LABEL_LAST,
  DETAIL_PAGE_WATCH_LABEL_NEXT,
} from '../../services/detail/index'
import {
  isGaiaPlusFeature,
  getFeaturedContent,
  isGuideNotStartedYet,
  isEpisode,
  isSeries,
  isGuide,
  getFeatureMedia,
} from '../../services/content/index'
import { isLiveAccessFeature } from '../../services/live-access-feature/index'
import { sendOptimizelyFeatureEvent } from '../../services/optimizely-feature/actions'
import { getLocalStorage, removeLocalStorage } from '../../services/local-preferences/index'
import { createRelative } from '../../services/url/index'

/**
 * Determine the type of page series/episode/video
 * @param {Boolean} isSeriesContent - is the current page a series page?
 * @param {Boolean} isVideoContent - is the current page a video page?
 * @returns {String} Returns the content type series/video/episode of the current page
 */
export const getContentNodeType = (isSeriesContent, isVideoContent) => {
  if (isSeriesContent) return TYPE_CONTENT_SERIES
  if (isVideoContent) return TYPE_CONTENT_VIDEO

  return TYPE_CONTENT_EPISODE
}

/**
 * Determine if the studio is "Gaia"
 * @param {Object} props - The props
 * @returns {Boolean} Whether or not the piece of content is a "Gaia original",
 *  based on the studio value
 */
export function isGaiaOriginal (props) {
  const { studio } = props
  const gaiaOriginalRegex = /^gaia$/i

  if (gaiaOriginalRegex.test(studio)) {
    return true
  }

  return false
}

/**
 * Build the upstream context for the watch events
 * @param {Object} props - The props
 * @returns {Map} updated upstream context with data from the video, series and MH
 */
export function buildJumbotronUpstreamContextGQL (props) {
  const {
    auth,
    nodeData,
    parentNodeData,
    nodeType,
    detailLocationType,
  } = props
  const isSeriesNode = nodeType === TYPE_CONTENT_SERIES
  const isEpisodeNode = nodeType === TYPE_CONTENT_EPISODE

  const upstreamContext = useSelector(selectUpstreamContext)
  const mhTileData = Map.isMap(upstreamContext) ? upstreamContext.getIn(['data', 'mhTileData'], Map()) : Map()
  const recommId = mhTileData.get('recommId')
  const storeKey = isSeriesNode ? STORE_KEY_DETAIL_FEATURED_EPISODE : SCREEN_TYPE_DETAIL_VIDEO
  const screenType = isSeriesNode ? SCREEN_TYPE_DETAIL_SERIES : CONTEXT_TYPE_VIDEO_ENTITY
  const screenParam = isSeriesNode ? parentNodeData.get('contentId') || parentNodeData.get('id') : nodeData.getIn(['contentId']) || nodeData.getIn(['id'])
  const title = isSeriesNode ? parentNodeData.get('title') : nodeData.getIn(['title'])
  const path = isSeriesNode ? parentNodeData.get('path') : nodeData.getIn(['path'])

  // GA4 events
  let playClickEventData = PLAY_CLICK_GA4_EVENT
    .mergeDeep({
      content_id: screenParam,
      content_type: nodeType,
      content_name: title,
      media_id: nodeData.getIn(['featureMedia', 'mediaId'], 0) || nodeData.getIn(['feature', 'id'], 0),
      media_language: parentNodeData.getIn(['primaryLanguage']) || parentNodeData.getIn(['contentLanguage']),
      is_featured_media: true,
      ui_element: 'Detail Page Primary',
      ui_location: getDetailPageUiLocationGQL(detailLocationType),
    })

  if (isEpisodeNode || isSeriesNode) {
    playClickEventData = playClickEventData.mergeDeep({
      parent_series_name: isEpisodeNode ? parentNodeData.get('seriesTitle') : parentNodeData.get('title'),
      parent_series_id: isEpisodeNode ? parentNodeData.get('seriesId') : parentNodeData.get('contentId'),
    })
  }

  // Add MH tile data if available
  if (Map.isMap(mhTileData) && mhTileData.size > 0) {
    const clickAttribution = mhTileData.get('clickAttribution') || null
    const spotlightType = mhTileData.get('spotlightType') || null
    const spotlightCampaign = mhTileData.get('spotlightCampaign') || null

    playClickEventData = playClickEventData.mergeDeep({
      row_placement: mhTileData.get('rowPlacement'),
      tile_number: mhTileData.get('tileNumber'),
      row_name: mhTileData.get('rowName'),
      ...(recommId && { recomm_id: recommId }),
      ...(clickAttribution && { click_attribution: clickAttribution }),
      ...(spotlightType && { spotlight_type: spotlightType }),
      ...(spotlightCampaign && { spotlight_campaign: spotlightCampaign }),
    })
  }

  // retrieve ai video from local storage
  const aiVideo = getLocalStorage('ai_video_clicked', auth.get('uid'), auth)
  const isAiRecommendation = aiVideo ? createRelative(path) === createRelative(aiVideo.path || '') : false
  let aiData = Map()
  if (isAiRecommendation) {
    // remove ai video from local storage
    removeLocalStorage('ai_video_clicked')
    aiData = aiData.mergeDeep({
      clickAttribution: 'AI Recommendation',
      isAiRecommendation: true,
    })
  }

  // Create the upstream context
  return createUpstreamContext({
    storeKey,
    screenType,
    screenParam,
    playEventData: mhTileData.size > 0 ? mhTileData : aiData,
    playClickEventData,
    contentId: screenParam,
  })
}

export const getJumbotronWatchEventLabel = (episodeData, staticText) => {
  const isFirst = episodeData.get('episode') === 1
  const isLiveAccess = isLiveAccessFeature(episodeData.get('feature', Map()))

  return isFirst && !isLiveAccess ? staticText.get('watchFirstFree') : staticText.get('signUpForEventsToWatch')
}

export const getJumbotronWatchEventLabelGQL = (episodeData, staticText) => {
  const isFirst = episodeData.get('episodeNumber') === 1
  const isLiveAccess = isGaiaPlusFeature(episodeData.get('featureMedia', Map()))

  return isFirst && !isLiveAccess ? staticText.get('watchFirstFree') : staticText.get('signUpForEventsToWatch')
}

/**
 * Get the Jumbotron Watch Label
 * @param {String} tileType - Tile type for the current detail page content
 * @param {Boolean} isNew - Is the featured episode new?
 * @param {String} detailPageType - Is the current page a series/video/episode?
 * @param {Map} staticText - staticText for the detail page
 * @returns {String} Text Label for the WatchLabel
 */
export function getJumbotronWatchTextLabel (tileType, isNew, detailPageType, staticText) {
  switch (detailPageType) {
    case TYPE_CONTENT_SERIES:
      return getJumbotronWatchSeriesLabel(tileType, isNew, staticText)
    case TYPE_CONTENT_EPISODE:
      return staticText.getIn(['watchThisEpisode'])
    case TYPE_CONTENT_VIDEO:
      return staticText.getIn(['watchThisVideo'])
    default:
      return null
  }
}

/**
 * Get the Jumbotron Series Watch Label
 * @param {String} tileType - Tile type for the current detail page content
 * @param {Boolean} isNew - Is the featured episode new?
 * @param {Map} staticText - staticText for the detail page
 * @returns {String} Text Label for the series WatchLabel
 */
function getJumbotronWatchSeriesLabel (tileType, isNew, staticText) {
  switch (tileType) {
    case DETAIL_PAGE_WATCH_LABEL_CONTINUE:
      return staticText.getIn(['continueEpisode'])
    case DETAIL_PAGE_WATCH_LABEL_FIRST:
      return staticText.getIn(['watchFirstEpisode'])
    case DETAIL_PAGE_WATCH_LABEL_LAST:
      return staticText.getIn(['watchLastEpisode'])
    case DETAIL_PAGE_WATCH_LABEL_NEXT:
      if (isNew) {
        return staticText.getIn(['watchLastEpisode'])
      }
      return staticText.getIn(['watchNextEpisode'])
    default:
      return null
  }
}

/**
 * Determine if a video is a meditation or yoga or fitness
 * @param {String} contentSubtype - Content Subtype for the current detail page content
 * @returns {Boolean} is or not Yoga or Meditation content
 */
export function isYogaOrMeditation (contentSubtype) {
  return contentSubtype === SUBTYPE_CONTENT_YOGA
    || contentSubtype === SUBTYPE_CONTENT_MEDITATION
    || contentSubtype === SUBTYPE_CONTENT_FITNESS
}

/**
 * GA event for detail page tag click
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Function} props.dispatch - Redux dispatch function
 * @param {String} props.tagName - The name of the tag
 * @param {String} props.tagId - The id of the tag
 * @param {String} props.title - The content title
 * @param {Number} props.id - The content id
 */
export function onClickDetailPageTag (props) {
  const {
    data,
    contentType,
    uiLocation,
    dispatch,
    tagName,
    tagId,
    title,
    id,
  } = props

  // GA4 event
  const seriesId = data.get('seriesId')
  let ga4EventData = TAG_CLICKED_GA4_EVENT.mergeDeep({
    content_id: id,
    content_type: contentType,
    content_name: title,
    tag_name: tagName,
    tag_id: tagId,
    ui_element: 'Detail Page Primary',
    ui_location: uiLocation,
  })

  if (seriesId && seriesId > 0) {
    ga4EventData = ga4EventData.mergeDeep({
      parent_series_name: data.get('seriesTitle'),
      parent_series_id: seriesId,
    })
  }

  dispatch(setEventDefault(ga4EventData))
}

export function getOptimizedImages (props) {
  const { url } = props
  let smallImageUrl = ''
  let mediumSmallImageUrl = ''
  let mediumImageUrl = ''
  let largeImageUrl = ''
  let xLargeImageUrl = ''
  let fallbackImageUrl = ''

  if (!url) {
    return {
      fallbackImageUrl,
      smallImageUrl,
      mediumSmallImageUrl,
      mediumImageUrl,
      largeImageUrl,
      xLargeImageUrl,
    }
  }

  try {
    const fallbackImageUrlObject = new URL(url)
    const imageUrlObject = new URL(url)
    const smallImageUrlObject = new URL(url)
    const mediumSmallImageUrlObject = new URL(url)
    const mediumImageUrlObject = new URL(url)
    const largeImageUrlObject = new URL(url)
    const xLargeImageUrlObject = new URL(url)
    const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
    const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
    const fallbackImageOptions = {
      ...parsedFallbackImageQuery,
      w: '900',
      fm: 'jpg',
      compress: 'auto',
    }
    const smallImageOptions = {
      ...parsedImageQuery,
      w: '480',
      fm: 'webp',
      q: '90',
    }
    const mediumSmallImageOptions = {
      ...parsedImageQuery,
      w: '608',
      fm: 'webp',
      q: '90',
    }
    const mediumImageOptions = {
      ...parsedImageQuery,
      w: '720',
      fm: 'webp',
      q: '90',
    }
    const largeImageOptions = {
      ...parsedImageQuery,
      w: '1000',
      fm: 'webp',
      q: '90',
    }
    const xLargeImageOptions = {
      ...parsedImageQuery,
      w: '1200',
      fm: 'webp',
      q: '90',
    }

    const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
    const stringifiedSmallImageQuery = stringifyQuery(smallImageOptions)
    const stringifiedMediumSmallImageQuery = stringifyQuery(mediumSmallImageOptions)
    const stringifiedMediumImageQuery = stringifyQuery(mediumImageOptions)
    const stringifiedLargeImageQuery = stringifyQuery(largeImageOptions)
    const stringifiedXLargeImageQuery = stringifyQuery(xLargeImageOptions)

    // set the search string back onto the fallback URL
    _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

    // set the search string back onto the image URLs
    _set(smallImageUrlObject, 'search', stringifiedSmallImageQuery)
    _set(mediumSmallImageUrlObject, 'search', stringifiedMediumSmallImageQuery)
    _set(mediumImageUrlObject, 'search', stringifiedMediumImageQuery)
    _set(largeImageUrlObject, 'search', stringifiedLargeImageQuery)
    _set(xLargeImageUrlObject, 'search', stringifiedXLargeImageQuery)

    fallbackImageUrl = fallbackImageUrlObject.toString()
    smallImageUrl = smallImageUrlObject.toString()
    mediumSmallImageUrl = mediumSmallImageUrlObject.toString()
    mediumImageUrl = mediumImageUrlObject.toString()
    largeImageUrl = largeImageUrlObject.toString()
    xLargeImageUrl = xLargeImageUrlObject.toString()
  } catch (e) {
    fallbackImageUrl = url
    smallImageUrl = url
    mediumSmallImageUrl = url
    mediumImageUrl = url
    largeImageUrl = url
    xLargeImageUrl = url
  }

  return {
    fallbackImageUrl,
    smallImageUrl,
    mediumSmallImageUrl,
    mediumImageUrl,
    largeImageUrl,
    xLargeImageUrl,
  }
}

/**
 * Get ui location param
 * @returns {String}
 */
export function getDetailPageUiLocationGQL (type) {
  return _startCase(`${type} detail page`)
}

export function getDetailPageUiElementGQL (isEpisodeDetail, isSeriesDetail) {
  if (isSeriesDetail) return 'Series Info'
  if (isEpisodeDetail) return 'Episode Info'

  return 'Video Info'
}

// works with GraphQL content data
export function getData (props) {
  const { data = Map() } = props
  const contentType = data.get('contentType')
  const displayType = data.get('displayType')
  const contentSubtype = data.get('contentSubtype')
  const featuredContent = getFeaturedContent({ data })
  const dataIsEpisode = isEpisode({ contentType, data })
  const dataIsSeries = isSeries({ contentType, data })
  const dataIsGuide = isGuide({ contentSubtype, displayType })
  let title = data.get('title')
  let url = data.get('path')
  let teaser = data.get('shortDescription')
  const isGuideAndNotStarted = isGuideNotStartedYet({ contentSubtype, displayType, data })

  if (isGuideAndNotStarted && !dataIsEpisode) {
    title = data.get('seriesTitle')
    url = data.get('seriesPath')
    teaser = data.get('seriesShortDescription')
  }

  if (dataIsSeries && !dataIsGuide) {
    title = featuredContent.get('title')
    url = featuredContent.get('path')
    teaser = featuredContent.get('shortDescription')
  }

  // if something went wrong, set to data title as last resort
  if (!title) {
    title = data.get('title')
  }

  if (!url) {
    url = data.get('path')
  }

  if (!teaser) {
    teaser = data.get('shortDescription')
  }

  return {
    title,
    url,
    teaser,
  }
}

// Get the node type (which is derived based on data)
export function getNodeType (props) {
  const { data } = props
  const contentType = data.get('contentType')
  const isEpisodeType = isEpisode({ contentType, data })
  const isSeriesType = isSeries({ contentType })
  let nodeType = TYPE_CONTENT_VIDEO

  if (isEpisodeType) {
    nodeType = TYPE_CONTENT_EPISODE
  }

  if (isSeriesType) {
    nodeType = TYPE_CONTENT_SERIES
  }

  return nodeType
}

/**
 * Determines whether to show winback buttons (user retention)
 * Winback buttons are shown when:
 * - User doesn't have watch access
 * - Upgrade button should not be shown
 * - Content has valid feature media
 *
 * @param {Object} props - Component properties
 * @param {Map} props.detailData - Detailed content data
 * @param {boolean} props.isEvent - Whether this is an event page
 * @param {boolean} props.hasLiveAccess - Whether user has live access
 * @param {boolean} props.isGuideDetailPage - Whether this is a guide page
 * @param {boolean} props.isWinbackFlowActive - Whether the winback flow is active
* @returns {boolean|null} true if buttons should be shown, false if not,
 * null if no feature media
 */
export function shouldShowWinbackButtons (props) {
  const {
    detailData,
    isEvent,
    hasLiveAccess,
    isGuideDetailPage,
    isWinbackFlowActive,
  } = props

  const contentType = detailData.get('contentType')
  const feature = getFeatureMedia({ data: detailData, contentType })

  // If no valid feature media, don't show buttons
  if (!Map.isMap(feature) || feature.size === 0) {
    return false
  }

  // Check if user already has watch access
  const hasWatchAccess = !!feature.getIn(['contentPolicy', 'accessCheck', 'allowed'], false)
  if (hasWatchAccess) {
    return false
  }

  // Check if upgrade button should be shown instead of winback
  const isGaiaPlusOnly = isGaiaPlusFeature(feature)
  const isGuideOrEvent = isEvent || isGuideDetailPage
  const shouldShowUpgradeButton = !hasLiveAccess && isGaiaPlusOnly && isGuideOrEvent
  if (shouldShowUpgradeButton) {
    return false
  }

  // Show winback buttons if user doesn't have access and shouldn't see upgrade
  return isWinbackFlowActive
}

export function handleSignUpClicked (props) {
  const { dispatch } = props
  dispatch(setEventDefault(RESTART_SUBSCRIPTION_CLICKED_GA4_EVENT.mergeDeep({
    ui_element: 'Jumbotron',
  })))
  dispatch(sendOptimizelyFeatureEvent({ eventName: 'restart_subscription_clicked' }))
}
